import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatPence, SERVICES, useCheckBalance } from '@/features/payments';
import {
  BalanceDisplay,
  InsufficientBalance,
} from '@/features/payments/components/insufficient-balance';

interface PayForSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchase: () => void;
  onAddFunds: () => void;
  isPurchasing: boolean;
}

const PRICE_PENCE = 250; // £2.50

/**
 * Pay for Search Modal - shown before running screening checks.
 * Charges £2.50 for the initial screening.
 */
export function PayForSearchModal({
  open,
  onOpenChange,
  onPurchase,
  onAddFunds,
  isPurchasing,
}: PayForSearchModalProps) {
  const { data: balanceCheck, isLoading: isCheckingBalance } = useCheckBalance(
    SERVICES.PEP_SANCTIONS_SCREENING,
    open,
  );

  const hasSufficientBalance = balanceCheck?.sufficient ?? false;
  const currentBalance = balanceCheck?.currentBalancePence ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[700px]">
        <DialogHeader className="text-left">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            Run Initial Screening Against Selected Lists
          </DialogTitle>
          <DialogDescription className="pt-2 text-[16px] text-[#051923]">
            This will run all the checks you have selected, including any sanctions lists, PEP
            databases, adverse media searches, and Companies House lookups.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          {/* Price display */}
          <div className="px-1">
            <div className="flex items-center justify-between">
              <span className="text-[16px] font-medium text-[#051923]">
                Initial Screening Check
              </span>
              <span className="text-[20px] font-semibold text-[#131313]">
                {formatPence(PRICE_PENCE)}
              </span>
            </div>
            <p className="mt-1 text-left text-[14px] text-gray-500">
              Runs all selected checks and generates a preliminary risk assessment
            </p>
          </div>

          {/* Balance check */}
          {!isCheckingBalance &&
            (hasSufficientBalance ? (
              <BalanceDisplay currentBalancePence={currentBalance} />
            ) : (
              <InsufficientBalance
                currentBalancePence={currentBalance}
                requiredAmountPence={PRICE_PENCE}
                shortfall={balanceCheck?.shortfall ?? PRICE_PENCE}
                onAddFunds={onAddFunds}
              />
            ))}
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <Button
            onClick={onPurchase}
            disabled={!hasSufficientBalance || isPurchasing || isCheckingBalance}
            className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-none tracking-normal text-white hover:bg-[#131313]/90 disabled:opacity-50"
          >
            {isPurchasing ? 'Processing...' : 'Purchase Check'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
